import cx from 'classnames';
import { useEffect, type ReactNode } from 'react';

import { Popper } from '@sb/design-system';

import { ChatMenuItem, type ChatMenuItemDef } from './ChatMenuItem';
import { useChatPanelOptional } from './ChatPanelProvider';

interface ChatPopoverMenuProps {
  isOpen: boolean;
  onClose: () => void;
  items: ChatMenuItemDef[];
  ariaLabel: string;
  /** Applied to the popper's reference wrapper, which sits in the caller's layout. */
  className?: string;
  'data-testid'?: string;
  /** The trigger — typically a kebab button. */
  children: ReactNode;
}

/**
 * Kebab popover shared by the chat header and the chat-history rows.
 *
 * Built on the design-system `Popper` so the menu escapes the chat
 * card's nested `overflow` ancestors and flips above the trigger when
 * it can't fit below. `ChatPanelOverlay` supplies the portal container
 * that keeps it above the panel.
 */
export function ChatPopoverMenu({
  isOpen,
  onClose,
  items,
  ariaLabel,
  className,
  'data-testid': dataTestId,
  children,
}: ChatPopoverMenuProps) {
  const hasPendingConfirmation =
    useChatPanelOptional()?.pendingConfirmation != null;

  // Close this menu if a confirmation modal appears asynchronously after it's been opened.
  useEffect(() => {
    if (isOpen && hasPendingConfirmation) onClose();
  }, [isOpen, hasPendingConfirmation, onClose]);

  return (
    <Popper
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom-end"
      // Reproduces the trigger-height + 4px gap both call sites used
      // when they positioned the popover by hand.
      offset={4}
      className={className}
      content={
        <div
          role="menu"
          aria-label={ariaLabel}
          data-testid={dataTestId}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          className={cx('tw-flex', 'tw-flex-col', 'tw-gap-2', 'tw-p-4')}
          style={{
            // 150px min-width, 12px radius, the layered shadow and the
            // 6%-black border don't have tokens (the radius scale jumps
            // 10 → 14).
            minWidth: 150,
            background: 'var(--surface-primary-elevated)',
            borderRadius: 12,
            boxShadow:
              '0 12px 32px -8px rgba(0,0,0,0.18), 0 4px 12px -4px rgba(0,0,0,0.10)',
            border: '1px solid var(--divider-primary)',
          }}
        >
          {items.map((item) => (
            <ChatMenuItem key={item.label} item={item} onAfterClick={onClose} />
          ))}
        </div>
      }
    >
      {children}
    </Popper>
  );
}
